import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tokenize, PaymentGateway } from '@idonatedev/idonate-sdk';
import { AppSettings } from '../../app.settings';
import { Sdk } from '../../core/sdk.service';
import { Donation } from '../../transaction-store/donation';
import { OptIns } from '../../transaction-store/opt-ins';

const PAYPAL_BUTTON_CONTAINER_ID = 'paypal-button-container';

// The SDK's PayPalTokenizer reads `currency`, `onCreateOrder` and
// `enableVenmo` off the container at runtime, but the published
// TokenizerContainer type omits them.
// This local extension restores those fields without loosening the call site.
interface PayPalTokenizerContainer extends tokenize.TokenizerContainer {
  currency?: string;
  onCreateOrder?: () => tokenize.PayPalCreateOrderData;
  enableVenmo?: boolean;
}

@Component({
  selector: 'paypal-checkout',
  template: `
    <div class="paypal-checkout-container">
      <div
        [id]="containerId"
        class="paypal-button-container"
        [ngClass]="{
          'paypal-buttons-disabled': !isFormValid,
          'paypal-buttons-processing': isProcessing
        }"
      ></div>
    </div>
  `,
  styles: [
    `
      .paypal-checkout-container {
        padding: 10px 0;
      }
      .paypal-button-container {
        min-height: 50px;
      }
      .paypal-buttons-disabled,
      .paypal-buttons-processing {
        opacity: 0.5;
        pointer-events: none;
      }
    `,
  ],
})
export class PayPalCheckoutComponent implements AfterViewInit, OnDestroy {
  @Input() isFormValid = false;
  @Output() tokenReady = new EventEmitter<tokenize.PaymentToken>();
  @Output() tokenError = new EventEmitter<Error>();
  @Output() validationFailed = new EventEmitter<void>();

  @Select() donation$: Observable<Donation>;
  @Select() optIns$: Observable<OptIns>;

  containerId = PAYPAL_BUTTON_CONTAINER_ID;
  isProcessing = false;

  private tokenizer: tokenize.PayPalTokenizer | null = null;
  private donationAmount = 0;
  private feeAmount = 0;
  private currencyCode = 'USD';

  constructor(private sdk: Sdk, private settings: AppSettings) {
    const organization = this.settings.getValue('organization');
    this.currencyCode = organization.currency_code || 'USD';

    // Track donation amount reactively so the popup always shows the live value
    // (PayPal SDK reads it inside the click-time onCreateOrder callback).
    this.donation$.subscribe((donation) => {
      this.donationAmount = donation.amount;
    });

    // When the donor opts to cover the processing fee, it is added to the
    // charge at submission (give-button: donation.amount + feeAmount). The
    // PayPal order must be created with that same total, otherwise PayPal
    // rejects the capture with an amount mismatch.
    this.optIns$.subscribe((optIns) => {
      this.feeAmount = optIns.payFee
        ? parseFloat(optIns.feeAmount.toFixed(2))
        : 0;
    });
  }

  async ngAfterViewInit() {
    const paypalGateway: PaymentGateway = this.settings.getValue('paypalGateway');
    if (!paypalGateway || paypalGateway.backendName !== 'paypal_checkout') {
      const err = new Error('PayPal Checkout gateway is not configured');
      this.tokenError.emit(err);
      return;
    }

    try {
      const tokenizer = (await this.sdk.client.createTokenizer(paypalGateway, {
        containerId: this.containerId,
        currency: this.currencyCode,
        // Venmo is always available with paypal_checkout (policy) — pin it
        // rather than riding the SDK default.
        enableVenmo: true,
        onCreateOrder: () => {
          if (!this.isFormValid) {
            this.validationFailed.emit();
            // Throwing here causes the SDK to reject popup creation; the
            // PayPal window never opens.
            throw new Error('Form is not valid');
          }
          return {
            amount: this.donationAmount + this.feeAmount,
            currency: this.currencyCode,
          };
        },
      } as PayPalTokenizerContainer)) as tokenize.PayPalTokenizer;

      this.tokenizer = tokenizer;

      tokenizer.on('tokenReady', (token: tokenize.PaymentToken) => {
        this.isProcessing = true;
        this.tokenReady.emit(token);
      });

      tokenizer.on('error', (err: Error) => {
        this.isProcessing = false;
        this.tokenError.emit(err);
      });
    } catch (err) {
      this.tokenError.emit(err instanceof Error ? err : new Error(String(err)));
    }
  }

  ngOnDestroy() {
    if (this.tokenizer) {
      this.tokenizer.destroy();
      this.tokenizer = null;
    }
  }
}
